import { Controller } from "@hotwired/stimulus"

const MEMBER_LIMIT = 6
const ASSET_LIMIT = 8

export default class extends Controller {
  static targets = ["input", "results"]
  static values = { members: Array }

  connect() {
    this.activeIndex = -1
    this.items = []
    this.activeToken = null
    this.abortController = null
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.boundDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocumentClick)
    this.abortPendingSearch()
  }

  handleInput() {
    this.activeToken = this.currentToken()

    if (!this.activeToken || this.activeToken.query.length < 1) {
      this.clearResults()
      return
    }

    if (this.activeToken.type === "mention") {
      this.renderMemberResults(this.activeToken.query)
    } else {
      this.searchAssets(this.activeToken.query)
    }
  }

  handleKeydown(event) {
    if (this.resultsTarget.hidden || this.items.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex + 1) % this.items.length
      this.renderResults()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex - 1 + this.items.length) % this.items.length
      this.renderResults()
    } else if (event.key === "Enter" || event.key === "Tab") {
      if (this.activeIndex >= 0) {
        event.preventDefault()
        this.selectIndex(this.activeIndex)
      }
    } else if (event.key === "Escape") {
      this.clearResults()
    }
  }

  select(event) {
    const index = Number(event.currentTarget.dataset.index)
    this.selectIndex(index)
  }

  pick(event) {
    const trigger = event.target.closest("[data-index]")
    if (!trigger || !this.element.contains(trigger)) return

    event.preventDefault()
    event.stopPropagation()

    const index = Number(trigger.dataset.index)
    if (Number.isNaN(index)) return

    this.selectIndex(index)
  }

  currentToken() {
    if (!this.hasInputTarget) return null

    const cursor = this.inputTarget.selectionStart ?? this.inputTarget.value.length
    const beforeCursor = this.inputTarget.value.slice(0, cursor)

    let start = beforeCursor.length - 1
    while (start >= 0 && !/\s/.test(beforeCursor[start])) start -= 1
    start += 1

    const token = beforeCursor.slice(start)
    if (!token.startsWith("!") && !token.startsWith("$")) return null

    return {
      type: token.startsWith("!") ? "mention" : "asset",
      query: token.slice(1),
      start,
      end: cursor
    }
  }

  renderMemberResults(query) {
    const normalized = query.toLowerCase()
    const matches = (this.membersValue || [])
      .filter(member => (member.alias || "").toLowerCase().includes(normalized))
      .sort((left, right) => this.memberScore(left.alias, normalized) - this.memberScore(right.alias, normalized))
      .slice(0, MEMBER_LIMIT)
      .map(member => ({
        kind: "member",
        label: `!${member.alias}`,
        meta: "channel member",
        alias: member.alias
      }))

    this.items = matches
    this.activeIndex = matches.length > 0 ? 0 : -1
    this.renderResults()
  }

  memberScore(alias, query) {
    const normalized = (alias || "").toLowerCase()
    if (normalized === query) return 0
    if (normalized.startsWith(query)) return 1

    const index = normalized.indexOf(query)
    return index >= 0 ? 10 + index : 999
  }

  async searchAssets(query) {
    this.abortPendingSearch()
    this.abortController = new AbortController()

    try {
      const response = await fetch(`/things/search?query=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abortController.signal
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const payload = await response.json()
      const thingItems = (payload.things || []).map(thing => ({
        kind: "asset",
        label: thing.ticker ? `$${String(thing.ticker).toUpperCase()}` : String(thing.title || "THING").toUpperCase(),
        meta: `thing // ${String(thing.title || "untitled").toUpperCase()}`,
        path: `/things/${thing.id}`
      }))

      const extraItems = (payload.extras || []).map(extra => ({
        kind: "asset",
        label: String(extra.title || "EXTRA").toUpperCase(),
        meta: "extra",
        path: `/extras/${extra.id}`
      }))

      this.items = [...thingItems, ...extraItems].slice(0, ASSET_LIMIT)
      this.activeIndex = this.items.length > 0 ? 0 : -1
      this.renderResults()
    } catch (error) {
      if (error.name === "AbortError") return

      this.items = []
      this.activeIndex = -1
      this.resultsTarget.hidden = false
      this.resultsTarget.innerHTML = '<div class="chewy-channel-compose-empty">search unavailable</div>'
    }
  }

  renderResults() {
    if (!this.hasResultsTarget) return

    if (this.items.length === 0) {
      this.resultsTarget.hidden = false
      this.resultsTarget.innerHTML = '<div class="chewy-channel-compose-empty">no matches</div>'
      return
    }

    this.resultsTarget.hidden = false
    this.resultsTarget.innerHTML = this.items.map((item, index) => `
      <button type="button"
              class="chewy-channel-compose-result${index === this.activeIndex ? " is-active" : ""}"
              data-index="${index}">
        <span class="chewy-channel-compose-result-label">${item.label}</span>
        <span class="chewy-channel-compose-result-meta">${item.meta}</span>
      </button>
    `).join("")
  }

  selectIndex(index) {
    const item = this.items[index]
    if (!item || !this.activeToken) return

    if (item.kind === "member") {
      this.replaceCurrentToken(`!${item.alias} `)
    } else {
      this.replaceCurrentToken(`[${item.label}](${item.path}) `)
    }

    this.clearResults()
    this.inputTarget.focus()
  }

  replaceCurrentToken(replacement) {
    const token = this.activeToken || this.currentToken()
    if (!token || !this.hasInputTarget) return

    const value = this.inputTarget.value
    const nextValue = `${value.slice(0, token.start)}${replacement}${value.slice(token.end)}`
    const nextCursor = token.start + replacement.length

    this.inputTarget.value = nextValue
    this.inputTarget.setSelectionRange(nextCursor, nextCursor)
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  handleDocumentClick(event) {
    if (this.element.contains(event.target)) return
    this.clearResults()
  }

  clearResults() {
    this.items = []
    this.activeIndex = -1
    this.activeToken = null
    this.abortPendingSearch()

    if (this.hasResultsTarget) {
      this.resultsTarget.hidden = true
      this.resultsTarget.innerHTML = ""
    }
  }

  abortPendingSearch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }
}
